import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatBusinessRelocationFullAddress } from './businessRelocationRfqAddressFormat';

function str(value) {
  if (value === null || value === undefined || String(value).trim() === '') return '—';
  return String(value);
}

function formatTotalEstimatedValue(rfq, formatNumber) {
  const amount = rfq?.totalValueUsd;
  if (amount == null || String(amount).trim() === '') return '—';
  const currency =
    rfq?.totalValueCurrency != null && String(rfq.totalValueCurrency).trim() !== ''
      ? String(rfq.totalValueCurrency).trim().toUpperCase()
      : 'USD';
  return `${formatNumber(amount)} ${currency}`;
}

/**
 * @param {{
 *   rfq: Record<string, unknown>,
 *   currentLoc: { country: string, state: string, city: string },
 *   destLoc: { country: string, state: string, city: string },
 *   relocationLabel: string,
 *   selectedServices: string[],
 *   formatSubmittedAt: (v: unknown) => string,
 *   formatDateSlash: (v: unknown) => string,
 *   logisticsModeLabel: (modeId: unknown) => string,
 *   formatNumber: (v: unknown) => string,
 * }} params
 */
export function downloadBusinessRelocationRfqPdf({
  rfq,
  currentLoc,
  destLoc,
  relocationLabel,
  selectedServices,
  formatSubmittedAt,
  formatDateSlash,
  logisticsModeLabel,
  formatNumber,
}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;
  let cursorY = 48;

  const ensureSpace = (needed = 40) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (cursorY + needed > pageHeight - 40) {
      doc.addPage();
      cursorY = 48;
    }
  };

  const addSectionTitle = (title) => {
    ensureSpace(36);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(15, 28, 51);
    doc.text(title, marginX, cursorY);
    cursorY += 10;
    doc.setDrawColor(226, 232, 240);
    doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
    cursorY += 14;
  };

  const addWrappedText = (label, value) => {
    const text = str(value);
    const maxWidth = pageWidth - marginX * 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(102, 112, 133);
    ensureSpace(18);
    doc.text(label, marginX, cursorY);
    cursorY += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(15, 28, 51);
    const lines = doc.splitTextToSize(text, maxWidth);
    ensureSpace(lines.length * 13 + 8);
    doc.text(lines, marginX, cursorY);
    cursorY += lines.length * 13 + 8;
  };

  const tableOpts = {
    margin: { left: marginX, right: marginX },
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 6, textColor: [15, 28, 51] },
    headStyles: { fillColor: [245, 247, 250], textColor: [102, 112, 133], fontStyle: 'bold' },
    columnStyles: { 0: { cellWidth: 150, fontStyle: 'bold' } },
  };

  const rfqRef = rfq.rfqId || rfq.rfqReference || '—';
  const statusLabel = String(rfq.status || 'pending')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(15, 28, 51);
  doc.text('Business Relocation RFQ', marginX, cursorY);
  cursorY += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(102, 112, 133);
  doc.text('Comprehensive office and employee relocation services', marginX, cursorY);
  cursorY += 22;

  addSectionTitle('Summary');
  autoTable(doc, {
    startY: cursorY,
    ...tableOpts,
    body: [
      ['RFQ ID', str(rfqRef)],
      ['Status', statusLabel],
      ['Submitted', str(formatSubmittedAt(rfq.createdAt || rfq.submittedDate))],
      ['Buyer', str(rfq.buyerName || rfq.contactPerson)],
      ['Company', str(rfq.companyName)],
      ['Contact', str(rfq.contactNumber)],
      ['Email', str(rfq.companyEmail)],
    ],
  });
  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 18;

  addSectionTitle('Type of Relocation');
  autoTable(doc, {
    startY: cursorY,
    ...tableOpts,
    body: [['Relocation type', str(relocationLabel)]],
  });
  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 18;

  addSectionTitle('Current Location');
  addWrappedText(
    'Address',
    formatBusinessRelocationFullAddress({
      address: rfq.currentAddress,
      country: currentLoc.country,
      state: currentLoc.state,
      city: currentLoc.city,
      pincode: rfq.currentPincode,
    }),
  );
  cursorY += 6;

  addSectionTitle('Destination Location');
  addWrappedText(
    'Address',
    formatBusinessRelocationFullAddress({
      address: rfq.destAddress,
      country: destLoc.country,
      state: destLoc.state,
      city: destLoc.city,
      pincode: rfq.destPincode,
    }),
  );
  cursorY += 6;

  addSectionTitle('Total Shipment Summary');
  autoTable(doc, {
    startY: cursorY,
    ...tableOpts,
    body: [
      ['Total Weight', `${formatNumber(rfq.totalWeightKg)} ${rfq.totalWeightUnit || 'KG'}`],
      ['Total Packages', formatNumber(rfq.totalPackages)],
      ['Total Value', formatTotalEstimatedValue(rfq, formatNumber)],
    ],
  });
  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 18;

  addSectionTitle('Timeline & Preferences');
  autoTable(doc, {
    startY: cursorY,
    ...tableOpts,
    body: [
      ['Preferred Move Date', formatDateSlash(rfq.preferredMoveDate)],
      ['Urgency Level', str(rfq.urgencyLevel)],
      ['Preferred Logistics Mode', logisticsModeLabel(rfq.logisticsMode)],
      ['Budget Range (USD)', str(rfq.budgetRange)],
    ],
  });
  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 12;
  addWrappedText('Special Requirements & Notes', rfq.specialNotes);

  addSectionTitle('Additional Services Required');
  const servicesText =
    selectedServices.length > 0 ? selectedServices.join(', ') : 'None selected.';
  addWrappedText('Services', servicesText);

  const fileName = `${String(rfqRef).replace(/[^\w-]+/g, '_') || 'business-relocation-rfq'}.pdf`;
  doc.save(fileName);
}
